import { useMemo } from 'react';
import {
  buildCreatorOutgoingCallRateOffer,
  findLatestCallRateOffer,
  mergeCallRateIntoCreatorInfo,
} from '../utils/callRateOfferUtils';
import { getCallRequestPricingDetails } from '../utils/callFormatUtils';

/**
 * Creator outgoing auto-call pricing derived from room + route (keeps business rules out of the screen).
 */
export function useCreatorOutgoingCallPricing({
  isCreator,
  shouldAutoPlaceCall,
  callMediaType,
  callRateOverride,
  matrixRoom,
  currentUser,
}) {
  const callRateOverrideFromRoom = useMemo(() => {
    if (!matrixRoom || isCreator) return null;
    return findLatestCallRateOffer({
      matrixRoom,
      mediaType: callMediaType,
      maxAgeMs: 5 * 60 * 1000,
    });
  }, [matrixRoom, isCreator, callMediaType]);

  const creatorOutgoingCallRateOffer = useMemo(() => {
    if (!isCreator || !shouldAutoPlaceCall || !callRateOverride) return null;
    return buildCreatorOutgoingCallRateOffer(callRateOverride);
  }, [isCreator, shouldAutoPlaceCall, callRateOverride]);

  const creatorInfoForOutgoingEligibility = useMemo(() => {
    if (!isCreator) return null;
    return mergeCallRateIntoCreatorInfo({
      creatorInfo: currentUser,
      mediaType: callMediaType,
      callRateOffer: creatorOutgoingCallRateOffer,
    });
  }, [isCreator, currentUser, callMediaType, creatorOutgoingCallRateOffer]);

  const creatorOutgoingPricing = useMemo(() => {
    if (!isCreator || !shouldAutoPlaceCall) return null;
    return getCallRequestPricingDetails(creatorInfoForOutgoingEligibility, callMediaType);
  }, [isCreator, shouldAutoPlaceCall, creatorInfoForOutgoingEligibility, callMediaType]);

  return {
    callRateOverrideFromRoom,
    creatorOutgoingCallRateOffer,
    creatorInfoForOutgoingEligibility,
    creatorOutgoingPricing,
  };
}
